import React, { useMemo, useState, useRef, useEffect } from 'react';
import './navbar.scss';
import { useLocation, useNavigate } from 'react-router-dom';
import * as FeatherIcons from 'react-feather';

interface MenuItem {
  id: number;
  label: string;
  icon?: string;
  link?: string;
  sequence_id?: number;
  subMenus?: any[];
  website?: string;
}

const toFeatherPascalCase = (name: string): string => {
  return name
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join('');
};

const renderIcon = (icon?: string) => {
  if (!icon || !icon.trim()) return null;
  const trimmed = icon.trim();
  if (trimmed.startsWith('fa ')) {
    const faName = trimmed.replace(/^fa fa-/, '').replace(/^fa-/, '');
    const PascalName = toFeatherPascalCase(faName);
    const FeatherIcon = (FeatherIcons as any)[PascalName];
    if (FeatherIcon)
      return <FeatherIcon size={13} style={{ marginRight: 4 }} />;
    return null;
  }
  if (trimmed.startsWith('bi ')) {
    return <i className={trimmed} style={{ marginRight: 4 }} />;
  }
  const PascalName = toFeatherPascalCase(trimmed);
  const FeatherIcon = (FeatherIcons as any)[PascalName];
  if (FeatherIcon) return <FeatherIcon size={13} style={{ marginRight: 4 }} />;
  return null;
};

const ANGULAR_URL =
  process.env.REACT_APP_API_environment === 'LOCAL'
    ? 'http://localhost:4200'
    : process.env.REACT_APP_API_environment === 'STAGING'
      ? 'https://tnemis-staging.tnsed.com'
      : 'https://tnemis.tnschools.gov.in';

export default function BottomNavbar() {
  const [activeMenu, setActiveMenu] = useState<MenuItem | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const bridgeReadyRef = useRef(false);
  const navbarRef = useRef<HTMLDivElement>(null);

  // Close popup when clicking outside the navbar
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (navbarRef.current && !navbarRef.current.contains(e.target as Node)) {
        setActiveMenu(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = `${ANGULAR_URL}/receiver.html`;
    iframe.onload = () => {
      bridgeReadyRef.current = true;
    };
    document.body.appendChild(iframe);
    iframeRef.current = iframe;

    return () => {
      if (iframeRef.current) {
        document.body.removeChild(iframeRef.current);
        iframeRef.current = null;
      }
    };
  }, []);

  // Navbar reads menu data from localStorage (written by Login on successful auth)
  // No API re-fetch needed — Login already stores MenuList, ReactMenus, and ModList

  const ANGULAR_RECEIVER = `${ANGULAR_URL}/receiver.html`;

  function clearAngularStorage(
    iframeRef: React.MutableRefObject<HTMLIFrameElement | null>
  ) {
    return new Promise<void>((resolve) => {
      const targetOrigin = new URL(ANGULAR_URL).origin;
      let done = false;

      const finish = () => {
        if (done) return;
        done = true;
        window.removeEventListener('message', listener);
        resolve();
      };

      const listener = (event: MessageEvent) => {
        if (event.origin === targetOrigin && event.data === 'clear_success') {
          finish();
        }
      };
      window.addEventListener('message', listener);

      const send = () => {
        iframeRef.current?.contentWindow?.postMessage(
          'COMMAND_CLEAR_STORAGE',
          targetOrigin
        );
      };

      if (iframeRef.current) {
        send();
      } else {
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = ANGULAR_RECEIVER;
        iframe.onload = send;
        document.body.appendChild(iframe);
        iframeRef.current = iframe;
      }

      setTimeout(finish, 1000);
    });
  }

  // const handleLogout = async () => {
  //   await clearAngularStorage(iframeRef);
  //   localStorage.clear();
  //   navigate('/login');
  // };

  // ✅ FIX: Mirrors Angular's getmenu() + getSubmenu() exactly, using loose == like Angular
  const getSubmenu = (items: any[], allowedIds: Set<string>): any[] => {
    if (!items) return [];
    const result: any[] = [];
    items.forEach((item) => {
      if (item?.subMenus && item.subMenus.length > 0) {
        // item has nested subMenus — recurse into them
        const filteredGroups = item.subMenus.map((group: any) => ({
          ...group,
          subMenuItems: getSubmenu(group.subMenuItems || [], allowedIds),
        }));
        result.push({ ...item, subMenus: filteredGroups });
      } else {
        // leaf item — must be in allowedIds
        // ✅ Use loose match like Angular: check both id forms
        if (item?.id != null && allowedIds.has(String(item.id))) {
          result.push(item);
        }
      }
    });
    return result;
  };

  const getAllReactMenuIds = (): Set<string> => {
    const reactMenus = JSON.parse(localStorage.getItem('ReactMenus') || '[]');
    const reactMenuChildren = JSON.parse(
      localStorage.getItem('ReactMenuChildren') || '[]'
    );
    const ids = new Set<string>();
    const walk = (nodes: any[]) => {
      if (!nodes) return;
      nodes.forEach((node) => {
        if (node?.id != null) ids.add(String(node.id));
        if (node?.subMenus) {
          node.subMenus.forEach((group: any) => {
            if (group?.subMenuItems) walk(group.subMenuItems);
          });
        }
      });
    };
    walk(reactMenus);
    // ReactMenuChildren are flat items with id — add them directly
    reactMenuChildren.forEach((child: any) => {
      if (child?.id != null) ids.add(String(child.id));
    });
    return ids;
  };

  // ✅ Check if menu item belongs to react (by website field OR ReactMenus set)
  const isReactMenu = (item: any): boolean => {
    if (!item) return false;
    if (item.website === 'tnemis-react') return true;
    return getAllReactMenuIds().has(String(item.id));
  };

  const menuItems = useMemo<MenuItem[]>(() => {
    try {
      const allMenus = JSON.parse(localStorage.getItem('MenuList') || '[]');
      const modListRaw = localStorage.getItem('ModList');

      if (!modListRaw) return allMenus;

      const modListParsed = JSON.parse(modListRaw);

      // ✅ FIX: Handle both string and array formats, mirror Angular's split(',')
      const modListStr = Array.isArray(modListParsed)
        ? modListParsed.join(',')
        : String(modListParsed);

      const modList: string[] = modListStr
        .split(',')
        .map((s: string) => s.trim())
        .filter((s: string) => s);

      // ✅ Build set for O(1) lookup — store as strings to match Angular's loose ==
      const allowedIds = new Set(modList.map((id) => id.trim()));

      // ✅ Mirror Angular's getmenu() exactly
      const filtered = allMenus
        .filter((menu: any) => allowedIds.has(String(menu.id)))
        .map((menu: any) => {
          if (menu.subMenus && menu.subMenus.length > 0) {
            return {
              ...menu,
              subMenus: menu.subMenus.map((group: any) => ({
                ...group,
                subMenuItems: getSubmenu(group.subMenuItems || [], allowedIds),
              })),
            };
          }
          return menu;
        });

      // ✅ Mirror Angular: for menu id==13 (Reports), filter subMenus[5] to only show ids 1004, 1020
      const idsToKeep = [1004, 1020];
      filtered.forEach((item: any) => {
        if (item.id == 13 && item.subMenus && item.subMenus.length > 5) {
          const reportGroup = item.subMenus[5];
          if (reportGroup && reportGroup.subMenuItems) {
            reportGroup.subMenuItems = reportGroup.subMenuItems.filter(
              (sub: any) => idsToKeep.includes(sub.id)
            );
          }
        }
      });

      // ✅ Mirror Angular: store reportmenuItem for other components
      localStorage.setItem('reportmenuItem', JSON.stringify(filtered));

      // ── Inject ReactMenuChildren as grouped dropdown sections ──
      // These are React items buried inside Angular parent menus (e.g.,
      // ITK Centre Support inside ITK Support). Group by _parentId and
      // create a synthetic menu entry ONLY if no matching parent exists in filtered.
      // If the parent already exists, the item is already visible inside its dropdown.
      const reactMenuChildren = JSON.parse(
        localStorage.getItem('ReactMenuChildren') || '[]'
      );
      if (reactMenuChildren.length > 0) {
        // Group children by parent
        const groupedByParent = new Map<
          string,
          { label: string; children: any[] }
        >();
        reactMenuChildren.forEach((child: any) => {
          const parentKey = String(child._parentId);
          if (!groupedByParent.has(parentKey)) {
            groupedByParent.set(parentKey, {
              label: child._parentLabel || 'Other',
              children: [],
            });
          }
          groupedByParent.get(parentKey)!.children.push(child);
        });

        groupedByParent.forEach((group, parentId) => {
          // Check if parent already exists in filtered menus
          const existingParent = filtered.find(
            (m: any) => String(m.id) === parentId
          );
          if (existingParent) {
            // Parent is already in the navbar — its children (including react ones)
            // are already rendered inside its dropdown. Skip to avoid duplication.
            return;
          }
          // Create a synthetic parent menu for the navbar
          filtered.push({
            id: Number(parentId) || parentId,
            label: group.label,
            icon: '',
            link: '',
            sequence_id: 99999,
            isMegaMenu: true,
            website: 'tnemis-react',
            subMenus: [{ subMenuItems: group.children }],
          });
        });
      }

      return filtered
        .sort((a: any, b: any) => (a.sequence_id ?? 0) - (b.sequence_id ?? 0))
        .filter((menu: any) => {
          // "Mail Request" menu (id=11128) only visible for user 4028619
          if (String(menu.id) === '11128') {
            const storedUsername = localStorage.getItem('username') || '';
            const backUser = localStorage.getItem('backUser') || '';
            // Show only if logged in as 4028619 AND not switched to another user via multi-login
            return storedUsername === '4028619' && !backUser;
          }
          return true;
        });
    } catch (err) {
      console.error('[React navbar] error:', err);
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const navigateToAngular = (link: string) => {
    const payload: Record<string, string | null> = {
      AuthorizationKey: localStorage.getItem('AuthorizationKey'),
      token: localStorage.getItem('token'),
      user_token: localStorage.getItem('token'),
      username: localStorage.getItem('username'),
      MenuList: localStorage.getItem('MenuList'),
      ModList: localStorage.getItem('ModList'),
      ReactMenus: localStorage.getItem('ReactMenus'),
      ReactMenuChildren: localStorage.getItem('ReactMenuChildren'),
      currentAy: localStorage.getItem('currentAy'),
      AUTH_BRIDGE_SYNC: 'true',
      BRIDGE_REDIRECT_URL: link,
    };

    // Always include backUser so Angular receiver knows whether to set or clear it
    const backUser = localStorage.getItem('backUser');
    payload.backUser = backUser || 'REMOVE';

    const sessionData = JSON.stringify(payload);

    let redirectDone = false;
    const redirectToAngular = () => {
      if (redirectDone) return;
      redirectDone = true;
      window.location.href = `${ANGULAR_URL}${link}`;
    };

    const handler = (event: MessageEvent) => {
      if (event.origin === ANGULAR_URL && event.data === 'success') {
        window.removeEventListener('message', handler);
        clearTimeout(fallbackTimer);
        redirectToAngular();
      }
    };
    window.addEventListener('message', handler);

    const fallbackTimer = setTimeout(() => {
      window.removeEventListener('message', handler);
      redirectToAngular();
    }, 1500);

    const send = () => {
      iframeRef.current?.contentWindow?.postMessage(sessionData, ANGULAR_URL);
    };

    if (bridgeReadyRef.current && iframeRef.current) {
      send();
    } else {
      if (!iframeRef.current) {
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = `${ANGULAR_URL}/receiver.html`;
        document.body.appendChild(iframe);
        iframeRef.current = iframe;
      }
      iframeRef.current!.onload = () => {
        bridgeReadyRef.current = true;
        send();
      };
    }
  };

  const handleMenuClick = (menu: MenuItem) => {
    if (menu.subMenus && menu.subMenus.length > 0) {
      setActiveMenu((prev) => (prev?.id === menu.id ? null : menu));
      return;
    }

    // ✅ website field is most reliable; fall back to ReactMenus set
    if (isReactMenu(menu)) {
      navigate(menu.link || '/');
    } else if (menu.link) {
      navigateToAngular(menu.link);
    }
  };

  // IDs for statically added external links (SMC, SLAS, MS-CMS) — open in new tab
  const EXTERNAL_LINK_IDS = [9999999, 9999998, 9999997];

  const handleSubMenuClick = (sub: any) => {
    // External links (SMC, SLAS, MS-CMS) open in new tab
    if (EXTERNAL_LINK_IDS.includes(Number(sub.id)) && sub.link) {
      console.log('sub.link', sub.link);
      window.open(sub.link, '_blank');
      setActiveMenu(null);
      return;
    }

    if (isReactMenu(sub)) {
      navigate(sub.link);
    } else if (sub.link) {
      navigateToAngular(sub.link);
    }
    setActiveMenu(null);
  };

  const handleNavbarClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).classList.contains('emis-nav')) {
      setActiveMenu(null);
    }
  };

  const hasSubMenus = (menu: MenuItem) =>
    menu.subMenus && menu.subMenus.length > 0;

  const isMenuActive = (menu: MenuItem): boolean => {
    const currentPath = location.pathname;
    if (menu.link && currentPath.startsWith(menu.link)) return true;
    if (menu.subMenus) {
      for (const group of menu.subMenus) {
        for (const item of group.subMenuItems || []) {
          if (item.link && currentPath.startsWith(item.link)) return true;
        }
      }
    }
    return false;
  };

  return (
    <div className="emis-navbar" ref={navbarRef}>
      <ul className="emis-nav" onClick={handleNavbarClick}>
        {menuItems
          .filter(
            (item: MenuItem) =>
              item.label?.trim() && (item as any).is_active !== 0
          )
          .map((item: MenuItem) => (
            <li
              key={item.id}
              className={`nav-item ${
                activeMenu?.id === item.id || isMenuActive(item)
                  ? 'active-menu'
                  : ''
              }`}
            >
              <span
                className="nav-link emis-link"
                onClick={() => handleMenuClick(item)}
              >
                {renderIcon(item.icon)}
                {item.label}
                {hasSubMenus(item) && (
                  <FeatherIcons.ChevronDown
                    size={14}
                    className={`chevron-icon ${activeMenu?.id === item.id ? 'chevron-open' : ''}`}
                  />
                )}
              </span>
            </li>
          ))}
      </ul>

      {activeMenu && (
        <div className="mega-menu-card">
          <div className="row">
            {activeMenu.subMenus?.map((group: any, index: number) => {
              const items = (group.subMenuItems || []).filter(
                (item: any) => !item.visible && item.is_active !== 0
              );
              if (items.length === 0) return null;

              return (
                <div key={index} className="col-md-3">
                  {items.map((item: any) =>
                    item.isTitle ? (
                      <div key={item.id} className="menu-title">
                        {item.label}
                      </div>
                    ) : (
                      <div
                        key={item.id}
                        className={`submenu-item${item.link && location.pathname.startsWith(item.link) ? ' submenu-active' : ''}`}
                      >
                        <span
                          className="submenu-link"
                          onClick={() => handleSubMenuClick(item)}
                        >
                          <span className="bullet">•</span>
                          {renderIcon(item.icon)}
                          <span className="submenu-label">{item.label}</span>
                          {/* <span className="chevron-arrow">›</span> */}
                        </span>
                      </div>
                    )
                  )}
                </div>
              );
            })}
          </div>

          {(!activeMenu.subMenus || activeMenu.subMenus.length === 0) && (
            <div className="text-muted" style={{ fontSize: '13px' }}>
              No Submenus Available
            </div>
          )}
        </div>
      )}
    </div>
  );
}
